import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { leadingZerosDateFormat } from "../../../../../common/helpers/formatDateTime";
import { LocalDate } from "../../../../../common/types/commonTypes";
import {
  CandidateStatusDetails,
  OverlappingAssignment,
} from "../../../TalentsStatusAPI";
import { hasPoolPro } from "../../../../talents/helpers";
import { PoolProPopper } from "../../../../talents/PoolProPopper";
import { TalentStatus } from "../../../../talents/types/common";
import { TalentNameWithPreview } from "./TalentNameWithPreview";

type Props = {
  bundleId?: string;
  talentId: string;
  fullName: string;
  status?: TalentStatus;
  statusChanged?: LocalDate;
  hasCoverLetter?: boolean;
  exclusivityEntryIds?: string[];
  rejectionMessageSent?: CandidateStatusDetails;
  warningMessages?: string[];
  overlappingAssignments?: OverlappingAssignment[];
};

export const TalentStatusDisplayName = ({
  bundleId,
  talentId,
  fullName,
  status,
  statusChanged,
  hasCoverLetter,
  exclusivityEntryIds,
  rejectionMessageSent,
  warningMessages = [],
  overlappingAssignments = [],
}: Props) => {
  const { t } = useTranslation("order");

  return (
    <Box display="flex" alignItems="center" gap="2px">
      <TalentNameWithPreview
        bundleId={bundleId}
        talentId={talentId}
        fullName={fullName}
        hasCoverLetter={hasCoverLetter}
        status={status}
        statusChanged={statusChanged}
        rejectionMessageSent={rejectionMessageSent}
      />
      {hasPoolPro({ exclusivityEntryIds }) && (
        <PoolProPopper talent={{ id: talentId }} />
      )}
      {overlappingAssignments.length > 0 && (
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" display="block" fontWeight={600}>
                {t("order:overlappingAssignments")}
              </Typography>
              {overlappingAssignments.map((assignment) => (
                <Typography
                  key={assignment.assignmentId}
                  variant="caption"
                  display="block"
                >
                  {format(parseISO(assignment.start), leadingZerosDateFormat)} -{" "}
                  {assignment.end
                    ? format(parseISO(assignment.end), leadingZerosDateFormat)
                    : t("order:openEndedAssignment")}
                </Typography>
              ))}
            </Box>
          }
          arrow
        >
          <IconButton size="small" disableRipple sx={{ color: "warning.main" }}>
            <EventBusyOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {warningMessages.length > 0 && (
        <Tooltip
          title={
            <Box>
              {warningMessages.map((message) => (
                <Typography key={message} variant="caption" display="block">
                  {message}
                </Typography>
              ))}
            </Box>
          }
          arrow
        >
          <IconButton size="small" disableRipple sx={{ color: "warning.main" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
